import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { CATEGORIES, DOMAINS } from '../data/data';
import { Seo, withTemplate, canonicalize } from '@/lib/seo';
import { fireEvent } from '@/lib/analytics';
import { AnimatedGradientText } from '@/components/ui/animated-gradient-text';

// Lightweight, optimized parallax engine scoped to this file
type Axis = 'y' | 'x' | 'both';
interface ParallaxOptions {
  speed?: number;
  axis?: Axis;
  maxTranslate?: number;
  rootMargin?: string;
}
interface ParallaxItem {
  element: HTMLElement;
  speed: number;
  axis: Axis;
  maxTranslate: number;
  inView: boolean;
  lastTransform: string;
}

const parallaxItems = new Set<ParallaxItem>();
let parallaxIo: IntersectionObserver | null = null;
let parallaxRafId: number | null = null;
let parallaxRunning = false;
let prefersReducedMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function ensureParallaxIO(rootMargin: string) {
  if (parallaxIo) return;
  parallaxIo = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const item = [...parallaxItems].find((it) => it.element === entry.target);
        if (!item) continue;
        item.inView = entry.isIntersecting;
      }
      requestParallaxTick();
    },
    { root: null, threshold: 0, rootMargin },
  );
}

function onParallaxScroll() {
  requestParallaxTick();
}

function onParallaxResize() {
  requestParallaxTick();
}

function startParallax() {
  if (parallaxRunning) return;
  parallaxRunning = true;
  window.addEventListener('scroll', onParallaxScroll, { passive: true });
  window.addEventListener('resize', onParallaxResize, { passive: true });
  requestParallaxTick();
}

function stopParallax() {
  if (!parallaxRunning) return;
  window.removeEventListener('scroll', onParallaxScroll);
  window.removeEventListener('resize', onParallaxResize);
  parallaxRunning = false;
}

function requestParallaxTick() {
  if (parallaxRafId !== null) return;
  parallaxRafId = window.requestAnimationFrame(updateParallax);
}

function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}

function updateParallax() {
  parallaxRafId = null;
  if (prefersReducedMotion) {
    parallaxItems.forEach((item) => {
      if (item.lastTransform !== '') {
        item.element.style.transform = '';
        item.lastTransform = '';
      }
    });
    return;
  }

  const viewportHeight = window.innerHeight || 0;
  const viewportWidth = window.innerWidth || 0;
  const centerY = viewportHeight / 2;
  const centerX = viewportWidth / 2;
  const farTop = -viewportHeight * 1.5;
  const farBottom = viewportHeight * 2.5;

  parallaxItems.forEach((item) => {
    const rect = item.element.getBoundingClientRect();

    if (rect.bottom < farTop || rect.top > farBottom) return;

    const elementCenterY = rect.top + rect.height / 2;
    const elementCenterX = rect.left + rect.width / 2;

    const dy = elementCenterY - centerY;
    const dx = elementCenterX - centerX;

    const translateY = clamp(-dy * item.speed, -item.maxTranslate, item.maxTranslate);
    const translateX = clamp(-dx * item.speed, -item.maxTranslate, item.maxTranslate);

    let transform = '';
    if (item.axis === 'y') {
      transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
    } else if (item.axis === 'x') {
      transform = `translate3d(${translateX.toFixed(2)}px, 0, 0)`;
    } else {
      transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0)`;
    }

    if (transform !== item.lastTransform) {
      item.element.style.transform = transform;
      item.lastTransform = transform;
    }
  });
}

function useParallaxRef(options: ParallaxOptions = {}) {
  const {
    speed = 0.06,
    axis = 'y',
    maxTranslate = 40,
    rootMargin = '20% 0px 20% 0px',
  } = options;
  const elRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    const el = elRef.current as HTMLElement | null;
    if (!el) return;

    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      prefersReducedMotion = mql.matches;
      requestParallaxTick();
    };
    mql.addEventListener?.('change', onChange);

    ensureParallaxIO(rootMargin);
    startParallax();

    const item: ParallaxItem = {
      element: el,
      speed,
      axis,
      maxTranslate,
      inView: false,
      lastTransform: '',
    };

    try {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const vm = Math.max(0, Math.min(0.5, parseFloat(rootMargin.split(' ')[0]) / 100 || 0.2));
      const extendedTop = -vh * vm;
      const extendedBottom = vh * (1 + vm);
      item.inView = rect.bottom >= extendedTop && rect.top <= extendedBottom;
    } catch {
      item.inView = true;
    }

    parallaxItems.add(item);
    parallaxIo?.observe(el);

    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    el.style.transformStyle = 'preserve-3d';

    updateParallax();

    return () => {
      mql.removeEventListener?.('change', onChange);
      parallaxIo?.unobserve(el);
      parallaxItems.delete(item);
      el.style.willChange = '';
      el.style.backfaceVisibility = '';
      el.style.transformStyle = '';
      el.style.transform = '';

      if (parallaxItems.size === 0) {
        stopParallax();
      }
    };
  }, [axis, maxTranslate, rootMargin, speed]);

  return elRef;
}

export default function Bundles() {
  // Track page view (P20250925-106)
  useEffect(() => {
    fireEvent('bundle_index_view', { bundle_count: CATEGORIES.length });
  }, []);

  // Subtle randomized, layered gradient background (same as Home)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    const rand = (min: number, max: number) => Math.random() * (max - min) + min;

    const hue = rand(205, 220);
    const saturation = rand(65, 78);
    const lightNearWhite = rand(96, 98);
    const lightBlue = rand(88, 92);
    const alphaSoft1 = rand(0.06, 0.1);
    const alphaSoft2 = rand(0.04, 0.08);

    const centerX = rand(30, 70);
    const centerY = rand(15, 45);

    const verticalAngle = 180;
    const horizontalAngle = 90;

    const color1 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, 1)`;
    const color2 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, 1)`;
    const tint1 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, ${alphaSoft1})`;
    const tint2 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2})`;

    const backgroundImage = [
      `radial-gradient(ellipse at ${centerX}% ${centerY}%, ${tint1} 0%, transparent 60%)`,
      `linear-gradient(${verticalAngle}deg, #ffffff 0%, ${color2} 100%)`,
      `linear-gradient(${horizontalAngle}deg, ${tint2} 0%, transparent 50%, ${tint2} 100%)`,
    ].join(', ');

    return {
      backgroundImage,
      backgroundColor: '#ffffff',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover, cover, cover',
      backgroundAttachment: 'scroll',
    };
  }, []);

  // Parallax refs
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 70 });
  const heroSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const bundlesGridRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 44 });

  // Animate cards immediately on mount, flying in from bottom
  const [isMounted, setIsMounted] = React.useState(false);
  
  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  // Custom animation variants for cards flying in from bottom
  const cardContainerVariants = {
    hidden: {},
    show: {
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const getCardVariants = (index: number) => ({
    hidden: { 
      opacity: 0, 
      y: 100, // Start from bottom
    },
    show: {
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.6, 
        ease: [0.16, 1, 0.3, 1] as const, // Custom easing for smooth motion
        delay: index * 0.05,
      },
    },
  });

  // Calculate bundle price by summing all available domain binPrices
  const calculateBundlePrice = (categorySlug: string) => {
    if (categorySlug === 'all') {
      return Object.values(DOMAINS)
        .flat()
        .filter((domain) => domain.status === 'available')
        .reduce((total, domain) => total + domain.binPrice, 0);
    }
    return (DOMAINS[categorySlug] || [])
      .filter((domain) => domain.status === 'available')
      .reduce((total, domain) => total + domain.binPrice, 0);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <>
      <Seo
        title={withTemplate('Domain bundles')}
        description="Hand-picked bundles with discounted pricing. Secure checkout through a trusted payment provider."
        canonical={canonicalize('/bundles')}
        og={{
          title: 'Domain bundles',
          description: 'Discounted sets with secure checkout.',
          url: canonicalize('/bundles'),
        }}
        twitter={{
          card: 'summary',
          title: 'Domain bundles',
          description: 'Discounted sets with secure checkout.',
        }}
      />

      <div className="min-h-screen py-20 relative overflow-hidden" style={backgroundStyle}>
        <div className="max-w-7xl mx-auto px-6 relative">
          {/* Header */}
          <div className="text-center mb-32 stagger-fade-1">
            <h1 ref={heroTitleRef as any} className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
              <Link to="/domains" className="inline-block hover:opacity-90 transition-opacity">
                <AnimatedGradientText colorFrom="#0000FF" colorTo="#000000" className="text-6xl md:text-7xl font-bold">
                  Domain
                </AnimatedGradientText>
              </Link>{' '}
              Categories
            </h1>
            <p ref={heroSubRef as any} className="text-3xl md:text-4xl text-foreground/70 leading-relaxed font-normal">
              Find the perfect domain for your professional service
            </p>
          </div>

          {/* Bundles Grid */}
          <motion.div
            ref={bundlesGridRef as any}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={cardContainerVariants}
            initial="hidden"
            animate={isMounted ? "show" : "hidden"}
          >
            {/* All Domains Bundle */}
            <motion.div variants={getCardVariants(0)}>
              <Link to="/domains" className="block group">
                <div className="apple-card p-8 h-full transition-all duration-300 group-hover:scale-105">
                <div className="flex items-end justify-end mb-6">
                  <ExternalLink className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors opacity-0 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>

                <h3 className="text-2xl font-bold text-black mb-3 group-hover:text-primary transition-all duration-300">
                  All Domains
                </h3>

                <p className="text-lg font-semibold text-attain-orange mb-4">
                  Complete collection
                </p>

                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Browse my entire collection of premium domains across all bundles. Perfect for
                  finding that perfect domain name for your project.
                </p>

                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl font-bold text-attain-orange">
                    {formatPrice(calculateBundlePrice('all'))}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    <strong className="text-primary text-lg">164</strong> domains available
                  </span>
                  <span className="text-primary group-hover:translate-x-2 transition-transform font-medium">
                    Explore →
                  </span>
                </div>
              </div>
            </Link>
            </motion.div>

            {/* Category Bundles */}
            {CATEGORIES.filter((cat) => cat.slug !== 'all').map((category, index) => (
              <motion.div key={category.slug} variants={getCardVariants(index + 1)}>
                <Link
                  to={`/bundle/${category.slug}`}
                  className="block group"
                >
                  <div className="apple-card p-8 h-full transition-all duration-300 group-hover:scale-105">
                  <div className="flex items-end justify-end mb-6">
                    <ExternalLink className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors opacity-0 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>

                  <h3 className="text-2xl font-bold text-black mb-3 group-hover:text-primary transition-all duration-300">
                    {category.title}
                  </h3>

                  <p className="text-lg font-semibold text-attain-orange mb-4">
                    {category.tagline}
                  </p>

                  <p className="text-muted-foreground mb-6 leading-relaxed">
                    {category.description}
                  </p>

                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl font-bold text-attain-orange">
                      {formatPrice(calculateBundlePrice(category.slug))}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      <strong className="text-primary text-lg">{category.count}</strong> domains
                      available
                    </span>
                    <span className="text-primary group-hover:translate-x-2 transition-transform font-medium">
                      Explore →
                    </span>
                  </div>
                </div>
              </Link>
              </motion.div>
            ))}
          </motion.div>

          {/* Stats Section */}
          <div className="mt-32 liquid-glass-card stagger-fade-4 relative overflow-hidden">
            <div className="relative z-10 p-16">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold liquid-gradient-text mb-6 drop-shadow-lg">
                  Domain Collection Stats
                </h2>
                <p className="text-muted-foreground text-xl">
                  My domain portfolio across industries
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
                <div className="text-center group liquid-interactive">
                  <div className="text-6xl font-bold liquid-gradient-text mb-4 group-hover:scale-110 transition-transform animate-liquid-glow">
                    164
                  </div>
                  <div className="text-muted-foreground text-lg">Total Domains</div>
                </div>
                <div className="text-center group liquid-interactive">
                  <div className="text-6xl font-bold text-attain-orange mb-4 group-hover:scale-110 transition-transform">
                    10
                  </div>
                  <div className="text-muted-foreground text-lg">Bundles</div>
                </div>
                <div className="text-center group liquid-interactive">
                  <div className="text-6xl font-bold text-green-600 mb-4 group-hover:scale-110 transition-transform">
                    100%
                  </div>
                  <div className="text-muted-foreground text-lg">Premium Quality</div>
                </div>
                <div className="text-center group liquid-interactive">
                  <div className="text-6xl font-bold text-purple-600 mb-4 group-hover:scale-110 transition-transform">
                    24/7
                  </div>
                  <div className="text-muted-foreground text-lg">Support</div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-20 text-center stagger-fade-4">
            <div className="liquid-glass-card bg-gradient-to-br from-attain-blue via-purple-600 to-attain-dark-blue text-white relative overflow-hidden animate-liquid-glow">
              <div className="relative z-10 p-16">
                <h2 className="text-4xl font-bold mb-6 drop-shadow-lg">
                  Can't Find What You're Looking For?
                </h2>
                <p className="text-2xl text-white/90 mb-12 leading-relaxed drop-shadow-md">
                  I have access to thousands more domains. Get in touch for custom searches and
                  recommendations.
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Link
                    to="/contact"
                    className="liquid-glass-button bg-gradient-to-r from-white/20 to-white/10 hover:from-white/30 hover:to-white/20 text-white font-bold text-lg"
                  >
                    <span className="relative z-10">Contact Me</span>
                  </Link>
                  <Link
                    to="/bundle/all"
                    className="liquid-glass-button bg-gradient-to-r from-attain-orange to-attain-orange-dark hover:from-attain-orange-dark hover:to-attain-orange text-white font-bold text-lg"
                  >
                    <span className="relative z-10">Browse All Domains</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
